/**
 * GGJ agent brand mark — a stylised 4-leaf emerald emblem used anywhere
 * the AI agent speaks ("GGJ Reasoning v2" paraphrase header, Sourcing
 * intake greeting, clarify thread header, agent composer, etc.).
 *
 * Design (per user feedback 21-Apr, see Mobbin/Wrangle reference):
 *   - 4 rounded petals arranged around a centre circle, filled with an
 *     emerald → deep-teal vertical gradient so the mark reads as
 *     "clover / leafy AI" at any size.
 *   - Optional soft emerald glow ring so the mark pops against the
 *     ambient pastel intake gradient.
 *   - Accessible: `aria-label="GGJ Assistant"` by default; pass
 *     `aria-hidden` when used alongside textual branding to avoid
 *     redundant announcements.
 *
 * Size is controlled via Tailwind classNames (e.g. `h-5 w-5`, `h-6 w-6`)
 * — the underlying SVG uses `viewBox="0 0 24 24"` so it scales cleanly.
 */

interface AgentMarkProps {
  /** Tailwind-friendly sizing classes; fall back to `h-5 w-5`. */
  className?: string;
  /**
   * Render a soft emerald halo behind the mark — useful on hero
   * greetings, hidden for inline chat headers where density matters.
   */
  withHalo?: boolean;
  /**
   * Accessible label. Pass `null` to mark it `aria-hidden` (decorative)
   * which is the right choice when adjacent text already announces
   * "GGJ Assistant" / "Copilot".
   */
  ariaLabel?: string | null;
}

export function AgentMark({
  className = 'h-5 w-5',
  withHalo = false,
  ariaLabel = 'GGJ Assistant',
}: AgentMarkProps) {
  const a11y =
    ariaLabel === null
      ? { 'aria-hidden': true as const }
      : { role: 'img' as const, 'aria-label': ariaLabel };

  return (
    <span
      className={`relative inline-flex items-center justify-center ${className}`}
      {...a11y}
    >
      {withHalo ? (
        <span
          aria-hidden
          className="absolute inset-[-25%] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(16,185,129,0.18), rgba(16,185,129,0) 70%)',
          }}
        />
      ) : null}
      <svg
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        className="relative h-full w-full"
        fill="none"
      >
        <defs>
          <linearGradient id="ggj-agent-mark-leaf" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34D399" />
            <stop offset="55%" stopColor="#10B981" />
            <stop offset="100%" stopColor="#047857" />
          </linearGradient>
          <linearGradient id="ggj-agent-mark-core" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ECFDF5" />
            <stop offset="100%" stopColor="#A7F3D0" />
          </linearGradient>
        </defs>

        {/* 4 rounded petals — top, right, bottom, left */}
        <path
          d="M12 2.4c2.7 0 4.5 1.8 4.5 4.3 0 2.3-1.7 4.2-4.5 4.9-2.8-.7-4.5-2.6-4.5-4.9 0-2.5 1.8-4.3 4.5-4.3z"
          fill="url(#ggj-agent-mark-leaf)"
        />
        <path
          d="M21.6 12c0 2.7-1.8 4.5-4.3 4.5-2.3 0-4.2-1.7-4.9-4.5.7-2.8 2.6-4.5 4.9-4.5 2.5 0 4.3 1.8 4.3 4.5z"
          fill="url(#ggj-agent-mark-leaf)"
          opacity="0.92"
        />
        <path
          d="M12 21.6c-2.7 0-4.5-1.8-4.5-4.3 0-2.3 1.7-4.2 4.5-4.9 2.8.7 4.5 2.6 4.5 4.9 0 2.5-1.8 4.3-4.5 4.3z"
          fill="url(#ggj-agent-mark-leaf)"
          opacity="0.88"
        />
        <path
          d="M2.4 12c0-2.7 1.8-4.5 4.3-4.5 2.3 0 4.2 1.7 4.9 4.5-.7 2.8-2.6 4.5-4.9 4.5-2.5 0-4.3-1.8-4.3-4.5z"
          fill="url(#ggj-agent-mark-leaf)"
          opacity="0.95"
        />

        {/* Centre core — tiny emerald highlight keeps the mark readable
            at ~16 px sizes where petal detail gets muddy. */}
        <circle cx="12" cy="12" r="2" fill="url(#ggj-agent-mark-core)" />
        <circle cx="12" cy="12" r="2" fill="rgba(16,185,129,0.45)" />
      </svg>
    </span>
  );
}
